import React, { useEffect, useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Subscribe } from 'unstated';

import { COMMERCE_BACKEND_CCP } from '@atlassian/commerce-ui/rollout';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { BannerStateContainer } from '@confluence/banners';
import { useSessionData } from '@confluence/session-data';
import { SPAViewContext } from '@confluence/spa-view-context';
import {
	Attribution,
	isUnauthorizedError,
	TransparentErrorBoundary,
} from '@confluence/error-boundary';
import {
	ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE,
	ExperienceAbort,
	ExperienceSuccess,
	ExperienceFailure,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { TRIAL_COMPONENTS_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { markErrorAsHandled } from '@confluence/graphql';

import type { TrialDetailsQuery as TrialDetailsQueryType } from '../graphql/__types__/TrialDetailsQuery';
import { TrialDetailsQuery } from '../graphql/TrialDetailsQuery.graphql';
import { useCommerceBackendName } from '../useCommerceBackendName';
import { useIsPreDunningPeriod } from '../useIsPreDunningPeriod';
import { getLicenseConsumingUserCountFromQuery } from '../graphql/getLicenseConsumingUserCountFromQuery';
import { useBillingUrls } from '../useBillingUrls';

import { NonCompliantBannerMessage } from './NonCompliantBannerMessage';
import { CompliantBannerMessage } from './CompliantBannerMessage';

const BANNER_NAME = 'GracePeriodBanner';
export const NON_COMPLIANT_BANNER_HEIGHT = 56;
export const COMPLIANT_BANNER_HEIGHT = 48;
const FREE_USER_LIMIT = 10;

export const BannerComponent = () => {
	const { isLicensed, isLoggedIn, userId, edition } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const isTrialEdition =
		edition === ConfluenceEdition.STANDARD || edition === ConfluenceEdition.PREMIUM;

	const initialSkipConditions =
		!isLicensed || !isLoggedIn || !userId || !isTrialEdition || !isSiteAdmin;

	useEffect(() => {
		if (!initialSkipConditions) {
			experienceTracker.start({
				name: ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE,
			});
		}
	}, [experienceTracker, initialSkipConditions]);

	const {
		commerceBackendName,
		loading: commerceBackendNameLoading,
		error: commerceBackendNameError,
	} = useCommerceBackendName(initialSkipConditions);

	const canSkipTrialDetails = initialSkipConditions || commerceBackendName !== COMMERCE_BACKEND_CCP;

	const {
		data,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		TRIAL_COMPONENTS_EXPERIENCES.GRACE_PERIOD_BANNER_DISMISSED,
		// skip conditions
		canSkipTrialDetails,
	);
	const deserializedState = deserializeState(data);
	const { trialGracePeriodBannerDismissed } = deserializedState;

	const {
		data: trialDetailsData,
		loading: trialLoading,
		error: trialError,
	} = useQuery<TrialDetailsQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		TrialDetailsQuery,
		{
			skip: canSkipTrialDetails,
		},
	);

	const licenseConsumingUserCount = getLicenseConsumingUserCountFromQuery(trialDetailsData);
	const numberOfUsersOverFreeLimit = licenseConsumingUserCount - FREE_USER_LIMIT;
	const isOverFreeLimit = licenseConsumingUserCount > FREE_USER_LIMIT;

	const {
		isPreDunningPeriod,
		deactivationDate,
		loading: predunningLoading,
		error: predunningError,
	} = useIsPreDunningPeriod({ skip: canSkipTrialDetails });

	const canSkipBillingUrl = !isPreDunningPeriod || !deactivationDate;
	const { paymentFlowUrl, loading: urlLoading, error: urlErr } = useBillingUrls(canSkipBillingUrl);

	const bannerType: string =
		licenseConsumingUserCount > FREE_USER_LIMIT ? 'non-compliant' : 'compliant';

	// `TrialDetailsQuery` can throw Permission exception if initiater doesn't have sufficient permission
	// Permission error in this case is expected and FE should handle it gracefully - "markErrorAsHandled" and abort the experience instead of reporting as failures.
	if (trialError && isUnauthorizedError(trialError)) {
		markErrorAsHandled(trialError);
		return (
			<ExperienceAbort
				name={ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE}
				reason="Aborted due to insufficient permission"
			/>
		);
	}

	// the queries are dependent and only one error should occur at a time
	const error =
		commerceBackendNameError || onboardingStateError || trialError || predunningError || urlErr;

	if (error) {
		return (
			<ExperienceFailure
				name={ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE}
				error={error}
				attributes={{
					isSiteAdmin,
					commerceBackendName,
					licenseConsumingUserCount,
					isPreDunningPeriod,
					bannerType,
				}}
			/>
		);
	}

	const isLoading =
		commerceBackendNameLoading ||
		trialLoading ||
		predunningLoading ||
		urlLoading ||
		onboardingStateLoading;

	if (
		canSkipTrialDetails ||
		isLoading ||
		!isPreDunningPeriod ||
		!deactivationDate ||
		!paymentFlowUrl ||
		// compliant banner dismissal
		(!isOverFreeLimit && trialGracePeriodBannerDismissed)
	) {
		return null;
	}

	return (
		<Subscribe to={[BannerStateContainer]}>
			{(bannerState: BannerStateContainer) => {
				const bannerHeight = isOverFreeLimit
					? NON_COMPLIANT_BANNER_HEIGHT
					: COMPLIANT_BANNER_HEIGHT;

				bannerState?.show(BANNER_NAME, bannerHeight);

				return (
					<>
						{isOverFreeLimit ? (
							<NonCompliantBannerMessage
								linkToAddBilling={paymentFlowUrl}
								deactivationDate={deactivationDate.dateString}
								numberOfUsersOverFreeLimit={numberOfUsersOverFreeLimit}
								licenseConsumingUserCount={licenseConsumingUserCount}
								edition={edition}
							/>
						) : (
							<CompliantBannerMessage
								linkToAddBilling={paymentFlowUrl}
								deactivationDate={deactivationDate.date}
								licenseConsumingUserCount={licenseConsumingUserCount}
								edition={edition}
								hideBanner={() => bannerState?.hide(BANNER_NAME)}
							/>
						)}
						<ExperienceSuccess
							name={ONBOARDING_GRACE_PERIOD_BANNER_EXPERIENCE}
							attributes={{ bannerType }}
						/>
					</>
				);
			}}
		</Subscribe>
	);
};

export const GracePeriodBanner = () => {
	return (
		<TransparentErrorBoundary attribution={Attribution.GROWTH_LUNA}>
			<BannerComponent />
		</TransparentErrorBoundary>
	);
};
